import { useCallback, useMemo, useState } from "react";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { motion } from "framer-motion";
import { Bar, Line, Scatter } from "react-chartjs-2";

import { scoringConfigToMultiplierConfig } from "@/features/config/MultiplierConfigPanel";
import { useStore } from "@/store/store";
import {
  Button,
  ChartContainer,
  Charts2Col,
  CheckboxLabel,
  FormRow,
  HeatCell,
  NumberInput,
  GlassCard,
  CardHeader,
  CardTitle,
  Select,
  SimConfigCol,
  SimConfigGrid,
  SimResults,
  StatCardEl,
  StatCardLabel,
  StatCardValue,
  StatsGrid,
  SweepTable,
  SweepTableWrap,
  Tab,
  TabBar,
  TunerGrid,
  TunerLabel,
} from "@/ui/styled";
import { CHART_COLORS, CHART_GRID, CHART_TEXT, theme } from "@/ui/theme";

import {
  defaultGAConfig,
  type GAConfig,
  type GAResult,
  runGeneticAlgorithm,
} from "./geneticOptimizer";
import { type MonteCarloResult, runMonteCarlo } from "./runMonteCarlo";
import {
  type ABResult,
  defaultSimConfig,
  runABComparison,
  runParameterSweep,
  runSimulation,
  type ScoringConfig,
  type SimulationConfig,
  type SimulationResult,
  type SweepParam,
  type SweepResult,
} from "./runSimulation";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

// ── Tuning controls ────────────────────────────────────────────

const ScoringTuner = ({
  config,
  onChange,
}: {
  config: ScoringConfig;
  onChange: (c: ScoringConfig) => void;
}) => {
  const update = (key: keyof ScoringConfig, value: number | boolean) =>
    onChange({ ...config, [key]: value });

  return (
    <TunerGrid>
      <TunerLabel>
        Scale
        <NumberInput
          type="number"
          value={config.scale}
          min={1}
          max={200}
          onChange={(e) => update("scale", Number(e.target.value))}
        />
      </TunerLabel>
      <TunerLabel>
        Clamp min
        <NumberInput
          type="number"
          value={config.clampMin}
          min={-500}
          max={0}
          onChange={(e) => update("clampMin", Number(e.target.value))}
        />
      </TunerLabel>
      <TunerLabel>
        Clamp max
        <NumberInput
          type="number"
          value={config.clampMax}
          min={0}
          max={500}
          onChange={(e) => update("clampMax", Number(e.target.value))}
        />
      </TunerLabel>
      <TunerLabel>
        Activity weight
        <NumberInput
          type="number"
          value={config.activityWeight}
          min={0.1}
          max={5}
          step={0.1}
          onChange={(e) => update("activityWeight", Number(e.target.value))}
        />
      </TunerLabel>
      <TunerLabel>
        Activity β
        <NumberInput
          type="number"
          value={config.activityBeta}
          min={1}
          max={50}
          onChange={(e) => update("activityBeta", Number(e.target.value))}
        />
      </TunerLabel>

      {/* Upset */}
      <CheckboxLabel>
        <input
          type="checkbox"
          checked={config.upsetEnabled}
          onChange={(e) => update("upsetEnabled", e.target.checked)}
        />
        Upset bonus
      </CheckboxLabel>
      {config.upsetEnabled && (
        <>
          <TunerLabel>
            Threshold
            <NumberInput
              type="number"
              value={config.upsetThreshold}
              min={0.1}
              max={0.5}
              step={0.05}
              onChange={(e) => update("upsetThreshold", Number(e.target.value))}
            />
          </TunerLabel>
          <TunerLabel>
            Max bonus
            <NumberInput
              type="number"
              value={config.upsetMaxBonus}
              min={0.1}
              max={1}
              step={0.05}
              onChange={(e) => update("upsetMaxBonus", Number(e.target.value))}
            />
          </TunerLabel>
        </>
      )}

      {/* Streak */}
      <CheckboxLabel>
        <input
          type="checkbox"
          checked={config.streakEnabled}
          onChange={(e) => update("streakEnabled", e.target.checked)}
        />
        Streak bonus
      </CheckboxLabel>
      {config.streakEnabled && (
        <>
          <TunerLabel>
            Multiplier
            <NumberInput
              type="number"
              value={config.streakMultiplier}
              min={0.01}
              max={0.5}
              step={0.01}
              onChange={(e) => update("streakMultiplier", Number(e.target.value))}
            />
          </TunerLabel>
          <TunerLabel>
            Max bonus
            <NumberInput
              type="number"
              value={config.streakMaxBonus}
              min={0.1}
              max={1}
              step={0.05}
              onChange={(e) => update("streakMaxBonus", Number(e.target.value))}
            />
          </TunerLabel>
        </>
      )}

      {/* Fatigue */}
      <CheckboxLabel>
        <input
          type="checkbox"
          checked={config.fatigueEnabled}
          onChange={(e) => update("fatigueEnabled", e.target.checked)}
        />
        Fatigue (diminishing returns)
      </CheckboxLabel>
      {config.fatigueEnabled && (
        <TunerLabel>
          Half-life (games)
          <NumberInput
            type="number"
            value={config.fatigueHalfLife}
            min={5}
            max={100}
            onChange={(e) => update("fatigueHalfLife", Number(e.target.value))}
          />
        </TunerLabel>
      )}

      {/* Momentum */}
      <CheckboxLabel>
        <input
          type="checkbox"
          checked={config.momentumEnabled}
          onChange={(e) => update("momentumEnabled", e.target.checked)}
        />
        Momentum
      </CheckboxLabel>
      {config.momentumEnabled && (
        <>
          <TunerLabel>
            Factor
            <NumberInput
              type="number"
              value={config.momentumFactor}
              min={0.01}
              max={0.2}
              step={0.01}
              onChange={(e) => update("momentumFactor", Number(e.target.value))}
            />
          </TunerLabel>
          <TunerLabel>
            Cap
            <NumberInput
              type="number"
              value={config.momentumCap}
              min={0.1}
              max={1}
              step={0.05}
              onChange={(e) => update("momentumCap", Number(e.target.value))}
            />
          </TunerLabel>
        </>
      )}

      {/* Comeback */}
      <CheckboxLabel>
        <input
          type="checkbox"
          checked={config.comebackEnabled}
          onChange={(e) => update("comebackEnabled", e.target.checked)}
        />
        Comeback bonus
      </CheckboxLabel>
      {config.comebackEnabled && (
        <>
          <TunerLabel>
            Threshold
            <NumberInput
              type="number"
              value={config.comebackThreshold}
              min={5}
              max={50}
              onChange={(e) => update("comebackThreshold", Number(e.target.value))}
            />
          </TunerLabel>
          <TunerLabel>
            Max bonus
            <NumberInput
              type="number"
              value={config.comebackMaxBonus}
              min={0.1}
              max={1}
              step={0.05}
              onChange={(e) => update("comebackMaxBonus", Number(e.target.value))}
            />
          </TunerLabel>
        </>
      )}

      {/* Consistency */}
      <CheckboxLabel>
        <input
          type="checkbox"
          checked={config.consistencyEnabled}
          onChange={(e) => update("consistencyEnabled", e.target.checked)}
        />
        Consistency bonus
      </CheckboxLabel>
      {config.consistencyEnabled && (
        <>
          <TunerLabel>
            σ threshold
            <NumberInput
              type="number"
              value={config.consistencySigmaThreshold}
              min={2}
              max={15}
              step={0.5}
              onChange={(e) => update("consistencySigmaThreshold", Number(e.target.value))}
            />
          </TunerLabel>
          <TunerLabel>
            Bonus
            <NumberInput
              type="number"
              value={config.consistencyBonus}
              min={0.05}
              max={0.5}
              step={0.05}
              onChange={(e) => update("consistencyBonus", Number(e.target.value))}
            />
          </TunerLabel>
        </>
      )}

      {/* Closeness */}
      <CheckboxLabel>
        <input
          type="checkbox"
          checked={config.closenessEnabled}
          onChange={(e) => update("closenessEnabled", e.target.checked)}
        />
        Closeness bonus
      </CheckboxLabel>
      {config.closenessEnabled && (
        <>
          <TunerLabel>
            Max Δμ
            <NumberInput
              type="number"
              value={config.closenessMaxDelta}
              min={0.1}
              max={2}
              step={0.1}
              onChange={(e) => update("closenessMaxDelta", Number(e.target.value))}
            />
          </TunerLabel>
          <TunerLabel>
            Bonus
            <NumberInput
              type="number"
              value={config.closenessBonus}
              min={0.05}
              max={0.5}
              step={0.05}
              onChange={(e) => update("closenessBonus", Number(e.target.value))}
            />
          </TunerLabel>
        </>
      )}
    </TunerGrid>
  );
};

// ── Stats cards ────────────────────────────────────────────────

const StatCard = ({ label, value, color }: { label: string; value: string; color?: string }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ type: "spring", stiffness: 400, damping: 25 }}
  >
    <StatCardEl>
      <StatCardValue style={{ color }}>{value}</StatCardValue>
      <StatCardLabel>{label}</StatCardLabel>
    </StatCardEl>
  </motion.div>
);

const AnalyticsSummary = ({ result }: { result: SimulationResult }) => {
  const a = result.analytics;
  const corrColor = (v: number) =>
    v > 0.8 ? theme.colors.success : v > 0.5 ? theme.colors.warning : theme.colors.danger;

  return (
    <StatsGrid>
      <StatCard
        label="Skill Correlation"
        value={a.skillCorrelation.toFixed(3)}
        color={corrColor(a.skillCorrelation)}
      />
      <StatCard
        label="Points Correlation"
        value={a.pointsCorrelation.toFixed(3)}
        color={corrColor(a.pointsCorrelation)}
      />
      <StatCard label="Skill MAE" value={a.skillMAE.toFixed(1)} />
      <StatCard label="Rank MAE" value={a.rankMAE.toFixed(2)} />
      <StatCard label="Convergence" value={`Match ${a.convergenceMatch}`} />
      <StatCard
        label="Prediction Acc."
        value={`${(a.predictionAccuracy * 100).toFixed(0)}%`}
        color={corrColor(a.predictionAccuracy)}
      />
      <StatCard label="Gini Coeff." value={a.giniCoefficient.toFixed(3)} />
      <StatCard label="Pts Volatility" value={a.pointsVolatility.toFixed(1)} />
      <StatCard
        label="ELO Baseline"
        value={a.eloBaselineCorrelation.toFixed(3)}
        color={corrColor(a.eloBaselineCorrelation)}
      />
    </StatsGrid>
  );
};

// ── Charts ─────────────────────────────────────────────────────

const TrueVsEstimatedChart = ({ result }: { result: SimulationResult }) => {
  const data = useMemo(
    () => ({
      datasets: [
        {
          label: "Players",
          data: result.players.map((p) => ({
            x: p.trueSkill,
            y: p.finalEstimatedSkill,
          })),
          backgroundColor: "#6366f1",
          pointRadius: 5,
        },
      ],
    }),
    [result],
  );

  const minSkill = Math.min(...result.players.map((p) => p.trueSkill)) - 5;
  const maxSkill = Math.max(...result.players.map((p) => p.trueSkill)) + 5;

  return (
    <ChartContainer>
      <h4>True Skill vs Estimated Skill</h4>
      <Scatter
        data={data}
        options={{
          responsive: true,
          plugins: { legend: { labels: { color: CHART_TEXT } } },
          scales: {
            x: {
              min: minSkill,
              max: maxSkill,
              title: { display: true, text: "True Skill", color: CHART_TEXT },
              ticks: { color: CHART_TEXT },
              grid: { color: CHART_GRID },
            },
            y: {
              min: minSkill,
              max: maxSkill,
              title: {
                display: true,
                text: "Estimated (μ-2σ)",
                color: CHART_TEXT,
              },
              ticks: { color: CHART_TEXT },
              grid: { color: CHART_GRID },
            },
          },
        }}
      />
      <p
        style={{
          fontSize: "0.7rem",
          color: theme.colors.textDim,
          marginTop: 4,
        }}
      >
        Closer to diagonal = better estimation
      </p>
    </ChartContainer>
  );
};

const TrueVsPointsChart = ({ result }: { result: SimulationResult }) => {
  const data = useMemo(
    () => ({
      datasets: [
        {
          label: "Players",
          data: result.players.map((p) => ({
            x: p.trueSkill,
            y: p.finalTotalPoints,
          })),
          backgroundColor: "#22c55e",
          pointRadius: 5,
        },
      ],
    }),
    [result],
  );

  return (
    <ChartContainer>
      <h4>True Skill vs Total Points</h4>
      <Scatter
        data={data}
        options={{
          responsive: true,
          plugins: { legend: { labels: { color: CHART_TEXT } } },
          scales: {
            x: {
              title: { display: true, text: "True Skill", color: CHART_TEXT },
              ticks: { color: CHART_TEXT },
              grid: { color: CHART_GRID },
            },
            y: {
              title: { display: true, text: "Total Points", color: CHART_TEXT },
              ticks: { color: CHART_TEXT },
              grid: { color: CHART_GRID },
            },
          },
        }}
      />
    </ChartContainer>
  );
};

const PointDistributionChart = ({ result }: { result: SimulationResult }) => {
  const data = useMemo(
    () => ({
      labels: result.analytics.pointDistribution.map((b) => `${b.bucketMin}`),
      datasets: [
        {
          label: "Match Points",
          data: result.analytics.pointDistribution.map((b) => b.count),
          backgroundColor: "#6366f199",
          borderColor: "#6366f1",
          borderWidth: 1,
        },
      ],
    }),
    [result],
  );

  return (
    <ChartContainer>
      <h4>Point Distribution (per match)</h4>
      <Bar
        data={data}
        options={{
          responsive: true,
          plugins: { legend: { labels: { color: CHART_TEXT } } },
          scales: {
            x: {
              title: { display: true, text: "Points", color: CHART_TEXT },
              ticks: { color: CHART_TEXT },
              grid: { color: CHART_GRID },
            },
            y: {
              title: { display: true, text: "Frequency", color: CHART_TEXT },
              ticks: { color: CHART_TEXT },
              grid: { color: CHART_GRID },
            },
          },
        }}
      />
    </ChartContainer>
  );
};

const ConvergenceChart = ({ result }: { result: SimulationResult }) => {
  const data = useMemo(() => {
    const labels = result.matchSnapshots.map((_, i) => `${i + 1}`);
    const avgErrors = result.matchSnapshots.map((snap) => {
      const errors = snap.playerSnapshots.map((p) => Math.abs(p.trueSkill - p.estimatedSkill));
      return errors.reduce((s, e) => s + e, 0) / errors.length;
    });
    return {
      labels,
      datasets: [
        {
          label: "Avg |True - Estimated|",
          data: avgErrors,
          borderColor: "#f59e0b",
          backgroundColor: "#f59e0b33",
          fill: true,
          tension: 0.3,
          pointRadius: 0,
        },
      ],
    };
  }, [result]);

  return (
    <ChartContainer>
      <h4>Skill Estimation Error Over Time</h4>
      <Line
        data={data}
        options={{
          responsive: true,
          plugins: { legend: { labels: { color: CHART_TEXT } } },
          scales: {
            x: {
              title: { display: true, text: "Match #", color: CHART_TEXT },
              ticks: { color: CHART_TEXT, maxTicksLimit: 20 },
              grid: { color: CHART_GRID },
            },
            y: {
              title: { display: true, text: "MAE", color: CHART_TEXT },
              ticks: { color: CHART_TEXT },
              grid: { color: CHART_GRID },
            },
          },
        }}
      />
    </ChartContainer>
  );
};

const SigmaChart = ({ result }: { result: SimulationResult }) => {
  const data = useMemo(
    () => ({
      labels: result.analytics.sigmaOverTime.map((_, i) => `${i + 1}`),
      datasets: [
        {
          label: "Avg σ",
          data: result.analytics.sigmaOverTime,
          borderColor: "#06b6d4",
          backgroundColor: "#06b6d433",
          fill: true,
          tension: 0.3,
          pointRadius: 0,
        },
      ],
    }),
    [result],
  );

  return (
    <ChartContainer>
      <h4>Average Uncertainty (σ) Over Time</h4>
      <Line
        data={data}
        options={{
          responsive: true,
          plugins: { legend: { labels: { color: CHART_TEXT } } },
          scales: {
            x: {
              title: { display: true, text: "Match #", color: CHART_TEXT },
              ticks: { color: CHART_TEXT, maxTicksLimit: 20 },
              grid: { color: CHART_GRID },
            },
            y: {
              title: { display: true, text: "σ", color: CHART_TEXT },
              ticks: { color: CHART_TEXT },
              grid: { color: CHART_GRID },
            },
          },
        }}
      />
    </ChartContainer>
  );
};

const MultiplierImpactChart = ({ result }: { result: SimulationResult }) => {
  const data = useMemo(() => {
    const keys = Object.keys(result.analytics.multiplierImpact);
    return {
      labels: keys.map((k) => k.charAt(0).toUpperCase() + k.slice(1)),
      datasets: [
        {
          label: "Avg |contribution|",
          data: keys.map((k) => result.analytics.multiplierImpact[k] ?? 0),
          backgroundColor: keys.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
          borderWidth: 0,
        },
      ],
    };
  }, [result]);

  return (
    <ChartContainer>
      <h4>Multiplier Impact (avg absolute contribution)</h4>
      <Bar
        data={data}
        options={{
          responsive: true,
          indexAxis: "y",
          plugins: { legend: { display: false } },
          scales: {
            x: { ticks: { color: CHART_TEXT }, grid: { color: CHART_GRID } },
            y: { ticks: { color: CHART_TEXT }, grid: { color: CHART_GRID } },
          },
        }}
      />
    </ChartContainer>
  );
};

const SkillTracesChart = ({ result }: { result: SimulationResult }) => {
  const data = useMemo(() => {
    const labels = result.matchSnapshots.map((_, i) => `${i + 1}`);
    const sortedPlayers = [...result.players].sort((a, b) => b.trueSkill - a.trueSkill);
    const top = sortedPlayers.slice(0, 8);
    const datasets = top.map((p, i) => {
      const skills = result.matchSnapshots.map((snap) => {
        const ps = snap.playerSnapshots.find((s) => s.playerId === p.id);
        return ps?.estimatedSkill ?? 0;
      });
      return {
        label: `${p.name} (true: ${p.trueSkill.toFixed(0)})`,
        data: skills,
        borderColor: CHART_COLORS[i % CHART_COLORS.length],
        tension: 0.3,
        pointRadius: 0,
        borderWidth: 2,
      };
    });
    return { labels, datasets };
  }, [result]);

  return (
    <ChartContainer>
      <h4>Skill Traces (top 8 by true skill)</h4>
      <Line
        data={data}
        options={{
          responsive: true,
          plugins: { legend: { labels: { color: CHART_TEXT } } },
          scales: {
            x: {
              title: { display: true, text: "Match #", color: CHART_TEXT },
              ticks: { color: CHART_TEXT, maxTicksLimit: 20 },
              grid: { color: CHART_GRID },
            },
            y: {
              title: {
                display: true,
                text: "Estimated Skill",
                color: CHART_TEXT,
              },
              ticks: { color: CHART_TEXT },
              grid: { color: CHART_GRID },
            },
          },
        }}
      />
    </ChartContainer>
  );
};

// ── New advanced charts ────────────────────────────────────────

const WinRateByTierChart = ({ result }: { result: SimulationResult }) => {
  const data = useMemo(
    () => ({
      labels: result.analytics.winRateByTier.map(
        (t) => `${t.tier} (avg: ${t.avgTrueSkill.toFixed(0)})`,
      ),
      datasets: [
        {
          label: "Win Rate",
          data: result.analytics.winRateByTier.map((t) => t.winRate * 100),
          backgroundColor: ["#ef444499", "#f59e0b99", "#22c55e99"],
          borderColor: ["#ef4444", "#f59e0b", "#22c55e"],
          borderWidth: 1,
        },
      ],
    }),
    [result],
  );

  return (
    <ChartContainer>
      <h4>Win Rate by Skill Tier (%)</h4>
      <Bar
        data={data}
        options={{
          responsive: true,
          plugins: { legend: { display: false } },
          scales: {
            x: { ticks: { color: CHART_TEXT }, grid: { color: CHART_GRID } },
            y: {
              ticks: { color: CHART_TEXT },
              grid: { color: CHART_GRID },
              title: { display: true, text: "%", color: CHART_TEXT },
            },
          },
        }}
      />
    </ChartContainer>
  );
};

const RankingStabilityChart = ({ result }: { result: SimulationResult }) => {
  const data = useMemo(
    () => ({
      labels: result.analytics.rankingStability.map((_, i) => `${i + 2}`),
      datasets: [
        {
          label: "Top-K Overlap",
          data: result.analytics.rankingStability,
          borderColor: "#8b5cf6",
          backgroundColor: "#8b5cf633",
          fill: true,
          tension: 0.3,
          pointRadius: 0,
        },
      ],
    }),
    [result],
  );

  return (
    <ChartContainer>
      <h4>Ranking Stability (top-5 overlap)</h4>
      <Line
        data={data}
        options={{
          responsive: true,
          plugins: { legend: { labels: { color: CHART_TEXT } } },
          scales: {
            x: {
              title: { display: true, text: "Match #", color: CHART_TEXT },
              ticks: { color: CHART_TEXT, maxTicksLimit: 20 },
              grid: { color: CHART_GRID },
            },
            y: {
              min: 0,
              max: 1,
              title: {
                display: true,
                text: "Overlap Ratio",
                color: CHART_TEXT,
              },
              ticks: { color: CHART_TEXT },
              grid: { color: CHART_GRID },
            },
          },
        }}
      />
    </ChartContainer>
  );
};

const ConfidenceIntervalsChart = ({ result }: { result: SimulationResult }) => {
  const sorted = useMemo(
    () => [...result.analytics.confidenceIntervals].sort((a, b) => a.trueSkill - b.trueSkill),
    [result],
  );
  const coverage = sorted.filter((c) => c.withinCI).length / sorted.length;

  return (
    <ChartContainer>
      <h4>Confidence Intervals (μ ± 2σ) — Coverage: {(coverage * 100).toFixed(0)}%</h4>
      <div style={{ overflowX: "auto" }}>
        <SweepTableWrap>
          <SweepTable>
            <thead>
              <tr>
                <th>Player</th>
                <th>True</th>
                <th>Est.</th>
                <th>Lower</th>
                <th>Upper</th>
                <th>In CI?</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((c) => (
                <tr key={c.playerId}>
                  <td>{c.name}</td>
                  <td>{c.trueSkill.toFixed(1)}</td>
                  <td>{c.estimatedSkill.toFixed(1)}</td>
                  <td>{c.lower.toFixed(1)}</td>
                  <td>{c.upper.toFixed(1)}</td>
                  <td
                    style={{
                      color: c.withinCI ? theme.colors.success : theme.colors.danger,
                    }}
                  >
                    {c.withinCI ? "✓" : "✗"}
                  </td>
                </tr>
              ))}
            </tbody>
          </SweepTable>
        </SweepTableWrap>
      </div>
    </ChartContainer>
  );
};

const HeadToHeadTable = ({ result }: { result: SimulationResult }) => {
  const h2h = result.analytics.headToHead;
  const n = h2h.names.length;
  if (n > 12) return null; // Too many players for a table

  return (
    <ChartContainer>
      <h4>Head-to-Head Wins</h4>
      <div style={{ overflowX: "auto" }}>
        <SweepTableWrap>
          <SweepTable>
            <thead>
              <tr>
                <th>↓ vs →</th>
                {h2h.names.map((name, i) => (
                  <th key={i}>{name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {h2h.names.map((name, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 600 }}>{name}</td>
                  {h2h.wins[i]!.map((wins, j) => {
                    if (i === j)
                      return (
                        <td key={j} style={{ background: theme.colors.bgInput }}>
                          —
                        </td>
                      );
                    const total = wins + (h2h.wins[j]?.[i] ?? 0);
                    const ratio = total > 0 ? wins / total : 0.5;
                    return (
                      <HeatCell key={j} intensity={ratio}>
                        {wins}
                      </HeatCell>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </SweepTable>
        </SweepTableWrap>
      </div>
    </ChartContainer>
  );
};

const PerPlayerConvergenceChart = ({ result }: { result: SimulationResult }) => {
  const sorted = useMemo(
    () => [...result.analytics.perPlayerConvergence].sort((a, b) => a.trueSkill - b.trueSkill),
    [result],
  );

  const data = useMemo(
    () => ({
      labels: sorted.map((p) => `${p.name} (${p.trueSkill.toFixed(0)})`),
      datasets: [
        {
          label: "Convergence Match",
          data: sorted.map((p) => p.convergenceMatch),
          backgroundColor: sorted.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
          borderWidth: 0,
        },
      ],
    }),
    [sorted],
  );

  return (
    <ChartContainer>
      <h4>Per-Player Convergence (match # where estimate ≈ true ± 3)</h4>
      <Bar
        data={data}
        options={{
          responsive: true,
          plugins: { legend: { display: false } },
          scales: {
            x: { ticks: { color: CHART_TEXT }, grid: { color: CHART_GRID } },
            y: {
              title: { display: true, text: "Match #", color: CHART_TEXT },
              ticks: { color: CHART_TEXT },
              grid: { color: CHART_GRID },
            },
          },
        }}
      />
    </ChartContainer>
  );
};

// ── Parameter sweep UI ─────────────────────────────────────────

const SWEEPABLE_PARAMS: {
  key: SweepParam;
  label: string;
  defaults: number[];
}[] = [
  { key: "scale", label: "Scale", defaults: [10, 25, 50, 75, 100, 150] },
  { key: "activityBeta", label: "Activity β", defaults: [1, 3, 5, 10, 20, 30] },
  {
    key: "activityWeight",
    label: "Activity Weight",
    defaults: [0.5, 0.75, 1, 1.5, 2, 3],
  },
  {
    key: "upsetMaxBonus",
    label: "Upset Max Bonus",
    defaults: [0, 0.1, 0.2, 0.4, 0.6, 0.8],
  },
  {
    key: "streakMultiplier",
    label: "Streak Multiplier",
    defaults: [0, 0.02, 0.05, 0.08, 0.12, 0.2],
  },
  {
    key: "fatigueHalfLife",
    label: "Fatigue Half-life",
    defaults: [5, 10, 20, 40, 80],
  },
  { key: "clampMax", label: "Clamp Max", defaults: [25, 50, 100, 200, 500] },
];

const SweepPanel = ({ baseConfig }: { baseConfig: SimulationConfig }) => {
  const [selectedParam, setSelectedParam] = useState<SweepParam>("scale");
  const [sweepResult, setSweepResult] = useState<SweepResult | null>(null);
  const [running, setRunning] = useState(false);

  const runSweep = useCallback(() => {
    setRunning(true);
    requestAnimationFrame(() => {
      const paramDef = SWEEPABLE_PARAMS.find((p) => p.key === selectedParam);
      const values = paramDef?.defaults ?? [1, 5, 10, 20, 50];
      const result = runParameterSweep(baseConfig, selectedParam, values);
      setSweepResult(result);
      setRunning(false);
    });
  }, [baseConfig, selectedParam]);

  return (
    <div>
      <h3 style={{ marginBottom: 8, color: theme.colors.textMuted }}>🔬 Parameter Sweep</h3>
      <FormRow>
        <Select
          value={selectedParam}
          onChange={(e) => setSelectedParam(e.target.value as SweepParam)}
        >
          {SWEEPABLE_PARAMS.map((p) => (
            <option key={p.key} value={p.key}>
              {p.label}
            </option>
          ))}
        </Select>
        <Button onClick={runSweep} disabled={running}>
          {running ? "⏳..." : "Run Sweep"}
        </Button>
      </FormRow>

      {sweepResult && (
        <div style={{ overflowX: "auto", marginTop: 12 }}>
          <SweepTableWrap>
            <SweepTable>
              <thead>
                <tr>
                  <th>{sweepResult.param}</th>
                  <th>Skill r</th>
                  <th>Points r</th>
                  <th>Gini</th>
                  <th>Pred. Acc.</th>
                  <th>Conv. Match</th>
                  <th>Rank MAE</th>
                </tr>
              </thead>
              <tbody>
                {sweepResult.results.map((r) => (
                  <tr key={r.value}>
                    <td style={{ fontWeight: 600 }}>{r.value}</td>
                    <HeatCell intensity={Math.max(0, r.skillCorrelation)}>
                      {r.skillCorrelation.toFixed(3)}
                    </HeatCell>
                    <HeatCell intensity={Math.max(0, r.pointsCorrelation)}>
                      {r.pointsCorrelation.toFixed(3)}
                    </HeatCell>
                    <HeatCell intensity={1 - r.gini}>{r.gini.toFixed(3)}</HeatCell>
                    <HeatCell intensity={r.predictionAccuracy}>
                      {(r.predictionAccuracy * 100).toFixed(0)}%
                    </HeatCell>
                    <td>{r.convergenceMatch}</td>
                    <td>{r.rankMAE.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </SweepTable>
          </SweepTableWrap>
        </div>
      )}
    </div>
  );
};

// ── A/B Comparison UI ──────────────────────────────────────────

const ABPanel = ({ baseConfig }: { baseConfig: SimulationConfig }) => {
  const [abResult, setAbResult] = useState<ABResult | null>(null);
  const [running, setRunning] = useState(false);
  const [bOverrides, setBOverrides] = useState<Partial<ScoringConfig>>({
    upsetEnabled: false,
    streakEnabled: false,
  });

  const runAB = useCallback(() => {
    setRunning(true);
    requestAnimationFrame(() => {
      const configB: SimulationConfig = {
        ...baseConfig,
        scoring: { ...baseConfig.scoring, ...bOverrides },
      };
      const result = runABComparison(baseConfig, configB);
      setAbResult(result);
      setRunning(false);
    });
  }, [baseConfig, bOverrides]);

  return (
    <div>
      <h3 style={{ marginBottom: 8, color: theme.colors.textMuted }}>⚖️ A/B Comparison</h3>
      <p
        style={{
          fontSize: "0.8rem",
          color: theme.colors.textDim,
          marginBottom: 8,
        }}
      >
        Config A = current settings. Config B overrides:
      </p>
      <TunerGrid>
        <CheckboxLabel>
          <input
            type="checkbox"
            checked={!bOverrides.upsetEnabled}
            onChange={(e) => setBOverrides((o) => ({ ...o, upsetEnabled: !e.target.checked }))}
          />
          Disable upset in B
        </CheckboxLabel>
        <CheckboxLabel>
          <input
            type="checkbox"
            checked={!bOverrides.streakEnabled}
            onChange={(e) => setBOverrides((o) => ({ ...o, streakEnabled: !e.target.checked }))}
          />
          Disable streak in B
        </CheckboxLabel>
        <CheckboxLabel>
          <input
            type="checkbox"
            checked={bOverrides.fatigueEnabled ?? false}
            onChange={(e) => setBOverrides((o) => ({ ...o, fatigueEnabled: e.target.checked }))}
          />
          Enable fatigue in B
        </CheckboxLabel>
        <CheckboxLabel>
          <input
            type="checkbox"
            checked={bOverrides.momentumEnabled ?? false}
            onChange={(e) =>
              setBOverrides((o) => ({
                ...o,
                momentumEnabled: e.target.checked,
              }))
            }
          />
          Enable momentum in B
        </CheckboxLabel>
      </TunerGrid>
      <Button onClick={runAB} disabled={running} style={{ marginTop: 8 }}>
        {running ? "⏳..." : "Run A/B Test"}
      </Button>

      {abResult && (
        <div style={{ overflowX: "auto", marginTop: 12 }}>
          <SweepTableWrap>
            <SweepTable>
              <thead>
                <tr>
                  <th>Metric</th>
                  <th>Config A</th>
                  <th>Config B</th>
                  <th>Winner</th>
                  <th>Δ</th>
                </tr>
              </thead>
              <tbody>
                {abResult.comparison.map((c) => (
                  <tr key={c.metric}>
                    <td style={{ fontWeight: 500 }}>{c.metric}</td>
                    <td>{c.valueA.toFixed(3)}</td>
                    <td>{c.valueB.toFixed(3)}</td>
                    <td
                      style={{
                        color:
                          c.winner === "A"
                            ? theme.colors.success
                            : c.winner === "B"
                              ? theme.colors.warning
                              : theme.colors.textDim,
                        fontWeight: 700,
                      }}
                    >
                      {c.winner}
                    </td>
                    <td>
                      {c.delta >= 0 ? "+" : ""}
                      {c.delta.toFixed(3)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </SweepTable>
          </SweepTableWrap>
        </div>
      )}
    </div>
  );
};

// ── Tab type ───────────────────────────────────────────────────

type SimTab = "results" | "advanced" | "sweep" | "ab" | "montecarlo" | "ga";

// ── Monte Carlo Panel ──────────────────────────────────────────

const MonteCarloPanel = ({ baseConfig }: { baseConfig: SimulationConfig }) => {
  const [runs, setRuns] = useState(10);
  const [mcResult, setMcResult] = useState<MonteCarloResult | null>(null);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);

  const runMC = useCallback(() => {
    setRunning(true);
    setProgress(0);
    setTimeout(() => {
      const result = runMonteCarlo(baseConfig, runs, (completed, total) => {
        setProgress(Math.round((completed / total) * 100));
      });
      setMcResult(result);
      setRunning(false);
    }, 50);
  }, [baseConfig, runs]);

  const convergenceData = useMemo(() => {
    if (!mcResult) return null;
    const labels = mcResult.convergenceBands.map((b) => `${b.match}`);
    return {
      labels,
      datasets: [
        {
          label: "Mean Error",
          data: mcResult.convergenceBands.map((b) => b.mean),
          borderColor: "#f59e0b",
          backgroundColor: "transparent",
          tension: 0.3,
          pointRadius: 0,
          borderWidth: 2,
        },
        {
          label: "5th Percentile",
          data: mcResult.convergenceBands.map((b) => b.p5),
          borderColor: "rgba(245, 158, 11, 0.3)",
          backgroundColor: "rgba(245, 158, 11, 0.1)",
          fill: "+1",
          tension: 0.3,
          pointRadius: 0,
          borderWidth: 1,
          borderDash: [4, 4],
        },
        {
          label: "95th Percentile",
          data: mcResult.convergenceBands.map((b) => b.p95),
          borderColor: "rgba(245, 158, 11, 0.3)",
          backgroundColor: "rgba(245, 158, 11, 0.1)",
          fill: "-1",
          tension: 0.3,
          pointRadius: 0,
          borderWidth: 1,
          borderDash: [4, 4],
        },
      ],
    };
  }, [mcResult]);

  const sigmaData = useMemo(() => {
    if (!mcResult) return null;
    const labels = mcResult.sigmaBands.map((b) => `${b.match}`);
    return {
      labels,
      datasets: [
        {
          label: "Mean σ",
          data: mcResult.sigmaBands.map((b) => b.mean),
          borderColor: "#06b6d4",
          backgroundColor: "transparent",
          tension: 0.3,
          pointRadius: 0,
          borderWidth: 2,
        },
        {
          label: "5th %ile",
          data: mcResult.sigmaBands.map((b) => b.p5),
          borderColor: "rgba(6, 182, 212, 0.3)",
          backgroundColor: "rgba(6, 182, 212, 0.1)",
          fill: "+1",
          tension: 0.3,
          pointRadius: 0,
          borderWidth: 1,
          borderDash: [4, 4],
        },
        {
          label: "95th %ile",
          data: mcResult.sigmaBands.map((b) => b.p95),
          borderColor: "rgba(6, 182, 212, 0.3)",
          backgroundColor: "rgba(6, 182, 212, 0.1)",
          fill: "-1",
          tension: 0.3,
          pointRadius: 0,
          borderWidth: 1,
          borderDash: [4, 4],
        },
      ],
    };
  }, [mcResult]);

  return (
    <div>
      <h3 style={{ marginBottom: 8, color: theme.colors.textMuted }}>
        🎲 Monte Carlo Confidence Bands
      </h3>
      <p
        style={{
          fontSize: "0.8rem",
          color: theme.colors.textDim,
          marginBottom: 8,
        }}
      >
        Run N simulations with the same config to show mean + 5th/95th percentile bands.
      </p>
      <FormRow>
        <TunerLabel style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
          Runs:
          <NumberInput
            type="number"
            value={runs}
            min={3}
            max={100}
            onChange={(e) => setRuns(Math.max(3, Number(e.target.value)))}
          />
        </TunerLabel>
        <Button onClick={runMC} disabled={running}>
          {running ? `⏳ ${progress}%...` : "Run Monte Carlo"}
        </Button>
      </FormRow>

      {mcResult && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Summary stats */}
          <StatsGrid style={{ marginTop: 12 }}>
            <StatCard
              label="Skill r (mean)"
              value={mcResult.summaryStats.skillCorrelation.mean.toFixed(3)}
            />
            <StatCard
              label="Skill r [5-95]"
              value={`${mcResult.summaryStats.skillCorrelation.p5.toFixed(2)} – ${mcResult.summaryStats.skillCorrelation.p95.toFixed(2)}`}
            />
            <StatCard
              label="Points r (mean)"
              value={mcResult.summaryStats.pointsCorrelation.mean.toFixed(3)}
            />
            <StatCard label="Gini (mean)" value={mcResult.summaryStats.gini.mean.toFixed(3)} />
            <StatCard
              label="Pred. Acc. (mean)"
              value={`${(mcResult.summaryStats.predictionAccuracy.mean * 100).toFixed(0)}%`}
            />
            <StatCard
              label="Convergence (mean)"
              value={`Match ${mcResult.summaryStats.convergenceMatch.mean.toFixed(0)}`}
            />
          </StatsGrid>

          <Charts2Col>
            {convergenceData && (
              <ChartContainer>
                <h4>Convergence Error (mean + bands)</h4>
                <Line
                  data={convergenceData}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: { labels: { color: CHART_TEXT } },
                    },
                    scales: {
                      x: {
                        title: {
                          display: true,
                          text: "Match #",
                          color: CHART_TEXT,
                        },
                        ticks: { color: CHART_TEXT, maxTicksLimit: 20 },
                        grid: { color: CHART_GRID },
                      },
                      y: {
                        title: {
                          display: true,
                          text: "MAE",
                          color: CHART_TEXT,
                        },
                        ticks: { color: CHART_TEXT },
                        grid: { color: CHART_GRID },
                      },
                    },
                  }}
                />
              </ChartContainer>
            )}
            {sigmaData && (
              <ChartContainer>
                <h4>Sigma Uncertainty (mean + bands)</h4>
                <Line
                  data={sigmaData}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: { labels: { color: CHART_TEXT } },
                    },
                    scales: {
                      x: {
                        title: {
                          display: true,
                          text: "Match #",
                          color: CHART_TEXT,
                        },
                        ticks: { color: CHART_TEXT, maxTicksLimit: 20 },
                        grid: { color: CHART_GRID },
                      },
                      y: {
                        title: { display: true, text: "σ", color: CHART_TEXT },
                        ticks: { color: CHART_TEXT },
                        grid: { color: CHART_GRID },
                      },
                    },
                  }}
                />
              </ChartContainer>
            )}
          </Charts2Col>
        </motion.div>
      )}
    </div>
  );
};

// ── Genetic Algorithm Panel ────────────────────────────────────

const GAPanel = ({ baseConfig }: { baseConfig: SimulationConfig }) => {
  const updateMultiplierConfig = useStore((s) => s.updateMultiplierConfig);
  const [gaConfig, setGAConfig] = useState<GAConfig>({
    ...defaultGAConfig,
    simConfig: {
      numPlayers: baseConfig.numPlayers,
      numMatches: Math.min(baseConfig.numMatches, 60),
      teamSize: baseConfig.teamSize,
      numTeams: baseConfig.numTeams,
      trueSkillSpread: baseConfig.trueSkillSpread,
    },
  });
  const [gaResult, setGAResult] = useState<GAResult | null>(null);
  const [running, setRunning] = useState(false);
  const [currentGen, setCurrentGen] = useState(0);

  const runGA = useCallback(() => {
    setRunning(true);
    setCurrentGen(0);
    setTimeout(() => {
      const result = runGeneticAlgorithm(gaConfig, (gen) => {
        setCurrentGen(gen.generation + 1);
      });
      setGAResult(result);
      setRunning(false);
    }, 50);
  }, [gaConfig]);

  const fitnessData = useMemo(() => {
    if (!gaResult) return null;
    return {
      labels: gaResult.generations.map((g) => `${g.generation + 1}`),
      datasets: [
        {
          label: "Best Fitness",
          data: gaResult.generations.map((g) => g.best.fitness),
          borderColor: "#22c55e",
          tension: 0.3,
          pointRadius: 2,
          borderWidth: 2,
        },
        {
          label: "Avg Fitness",
          data: gaResult.generations.map((g) => g.avgFitness),
          borderColor: "#6366f1",
          tension: 0.3,
          pointRadius: 2,
          borderWidth: 2,
        },
      ],
    };
  }, [gaResult]);

  return (
    <div>
      <h3 style={{ marginBottom: 8, color: theme.colors.textMuted }}>
        🧬 Genetic Algorithm Config Optimizer
      </h3>
      <p
        style={{
          fontSize: "0.8rem",
          color: theme.colors.textDim,
          marginBottom: 8,
        }}
      >
        Evolves ScoringConfig to maximize skill correlation, fairness, and convergence speed.
      </p>
      <TunerGrid>
        <TunerLabel>
          Population
          <NumberInput
            type="number"
            value={gaConfig.populationSize}
            min={10}
            max={100}
            onChange={(e) =>
              setGAConfig((c) => ({
                ...c,
                populationSize: Math.max(10, Number(e.target.value)),
              }))
            }
          />
        </TunerLabel>
        <TunerLabel>
          Generations
          <NumberInput
            type="number"
            value={gaConfig.generations}
            min={5}
            max={200}
            onChange={(e) =>
              setGAConfig((c) => ({
                ...c,
                generations: Math.max(5, Number(e.target.value)),
              }))
            }
          />
        </TunerLabel>
        <TunerLabel>
          Mutation Rate
          <NumberInput
            type="number"
            value={gaConfig.mutationRate}
            min={0.01}
            max={0.5}
            step={0.01}
            onChange={(e) =>
              setGAConfig((c) => ({
                ...c,
                mutationRate: Number(e.target.value),
              }))
            }
          />
        </TunerLabel>
        <TunerLabel>
          Crossover Rate
          <NumberInput
            type="number"
            value={gaConfig.crossoverRate}
            min={0.1}
            max={1}
            step={0.05}
            onChange={(e) =>
              setGAConfig((c) => ({
                ...c,
                crossoverRate: Number(e.target.value),
              }))
            }
          />
        </TunerLabel>
      </TunerGrid>

      <h4
        style={{
          fontSize: "0.85rem",
          color: theme.colors.textMuted,
          marginTop: 12,
          marginBottom: 6,
        }}
      >
        Fitness Weights
      </h4>
      <TunerGrid>
        <TunerLabel>
          Skill Correlation
          <NumberInput
            type="number"
            value={gaConfig.fitnessWeights.skillCorrelation}
            min={0}
            max={1}
            step={0.05}
            onChange={(e) =>
              setGAConfig((c) => ({
                ...c,
                fitnessWeights: {
                  ...c.fitnessWeights,
                  skillCorrelation: Number(e.target.value),
                },
              }))
            }
          />
        </TunerLabel>
        <TunerLabel>
          Fairness (1 - Gini)
          <NumberInput
            type="number"
            value={gaConfig.fitnessWeights.fairness}
            min={0}
            max={1}
            step={0.05}
            onChange={(e) =>
              setGAConfig((c) => ({
                ...c,
                fitnessWeights: {
                  ...c.fitnessWeights,
                  fairness: Number(e.target.value),
                },
              }))
            }
          />
        </TunerLabel>
        <TunerLabel>
          Convergence Speed
          <NumberInput
            type="number"
            value={gaConfig.fitnessWeights.convergenceSpeed}
            min={0}
            max={1}
            step={0.05}
            onChange={(e) =>
              setGAConfig((c) => ({
                ...c,
                fitnessWeights: {
                  ...c.fitnessWeights,
                  convergenceSpeed: Number(e.target.value),
                },
              }))
            }
          />
        </TunerLabel>
        <TunerLabel>
          Prediction Acc.
          <NumberInput
            type="number"
            value={gaConfig.fitnessWeights.predictionAccuracy}
            min={0}
            max={1}
            step={0.05}
            onChange={(e) =>
              setGAConfig((c) => ({
                ...c,
                fitnessWeights: {
                  ...c.fitnessWeights,
                  predictionAccuracy: Number(e.target.value),
                },
              }))
            }
          />
        </TunerLabel>
      </TunerGrid>

      <FormRow style={{ marginTop: 12 }}>
        <Button onClick={runGA} disabled={running}>
          {running ? `⏳ Gen ${currentGen}/${gaConfig.generations}...` : "🧬 Find Optimal Config"}
        </Button>
      </FormRow>

      {gaResult && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ marginTop: 12 }}
        >
          <StatsGrid>
            <StatCard
              label="Best Fitness"
              value={gaResult.bestFitness.toFixed(3)}
              color={theme.colors.success}
            />
            <StatCard label="Skill r" value={gaResult.bestMetrics.skillCorrelation.toFixed(3)} />
            <StatCard label="Points r" value={gaResult.bestMetrics.pointsCorrelation.toFixed(3)} />
            <StatCard label="Gini" value={gaResult.bestMetrics.gini.toFixed(3)} />
            <StatCard
              label="Pred. Acc."
              value={`${(gaResult.bestMetrics.predictionAccuracy * 100).toFixed(0)}%`}
            />
            <StatCard
              label="Convergence"
              value={`Match ${gaResult.bestMetrics.convergenceMatch}`}
            />
          </StatsGrid>

          {fitnessData && (
            <ChartContainer>
              <h4>Evolution Progress</h4>
              <Line
                data={fitnessData}
                options={{
                  responsive: true,
                  plugins: { legend: { labels: { color: CHART_TEXT } } },
                  scales: {
                    x: {
                      title: {
                        display: true,
                        text: "Generation",
                        color: CHART_TEXT,
                      },
                      ticks: { color: CHART_TEXT },
                      grid: { color: CHART_GRID },
                    },
                    y: {
                      title: {
                        display: true,
                        text: "Fitness",
                        color: CHART_TEXT,
                      },
                      ticks: { color: CHART_TEXT },
                      grid: { color: CHART_GRID },
                    },
                  },
                }}
              />
            </ChartContainer>
          )}

          {/* Best config display */}
          <ChartContainer>
            <h4>Optimal Config Found</h4>
            <div style={{ overflowX: "auto" }}>
              <SweepTableWrap>
                <SweepTable>
                  <thead>
                    <tr>
                      <th>Parameter</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(gaResult.bestConfig)
                      .filter(([_, v]) => typeof v === "number" || typeof v === "boolean")
                      .map(([key, value]) => (
                        <tr key={key}>
                          <td style={{ fontWeight: 500 }}>{key}</td>
                          <td>
                            {typeof value === "boolean"
                              ? value
                                ? "✓"
                                : "✗"
                              : typeof value === "number"
                                ? value.toFixed(3)
                                : String(value)}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </SweepTable>
              </SweepTableWrap>
            </div>
          </ChartContainer>

          <FormRow style={{ marginTop: 12 }}>
            <Button
              onClick={() => {
                const mc = scoringConfigToMultiplierConfig(gaResult.bestConfig);
                updateMultiplierConfig(mc);
              }}
            >
              ✅ Apply to Point System
            </Button>
          </FormRow>
        </motion.div>
      )}
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────

export default function Simulation() {
  const [config, setConfig] = useState<SimulationConfig>({
    ...defaultSimConfig,
  });
  const [result, setResult] = useState<SimulationResult | null>(null);
  const [running, setRunning] = useState(false);
  const [activeTab, setActiveTab] = useState<SimTab>("results");

  const run = useCallback(() => {
    setRunning(true);
    requestAnimationFrame(() => {
      const r = runSimulation(config);
      setResult(r);
      setRunning(false);
      setActiveTab("results");
    });
  }, [config]);

  return (
    <GlassCard>
      <CardHeader>
        <CardTitle>🧪 Simulation &amp; Tuning</CardTitle>
      </CardHeader>

      <SimConfigGrid>
        <SimConfigCol>
          <h3>Match Setup</h3>
          <TunerGrid>
            <TunerLabel>
              Players
              <NumberInput
                type="number"
                value={config.numPlayers}
                min={2}
                max={50}
                onChange={(e) =>
                  setConfig((c) => ({
                    ...c,
                    numPlayers: Math.max(2, Number(e.target.value)),
                  }))
                }
              />
            </TunerLabel>
            <TunerLabel>
              Matches
              <NumberInput
                type="number"
                value={config.numMatches}
                min={10}
                max={2000}
                step={10}
                onChange={(e) =>
                  setConfig((c) => ({
                    ...c,
                    numMatches: Math.max(10, Number(e.target.value)),
                  }))
                }
              />
            </TunerLabel>
            <TunerLabel>
              Team size
              <NumberInput
                type="number"
                value={config.teamSize}
                min={1}
                max={10}
                onChange={(e) =>
                  setConfig((c) => ({
                    ...c,
                    teamSize: Math.max(1, Number(e.target.value)),
                  }))
                }
              />
            </TunerLabel>
            <TunerLabel>
              Teams/match
              <NumberInput
                type="number"
                value={config.numTeams}
                min={2}
                max={10}
                onChange={(e) =>
                  setConfig((c) => ({
                    ...c,
                    numTeams: Math.max(2, Number(e.target.value)),
                  }))
                }
              />
            </TunerLabel>
            <TunerLabel>
              Skill spread (σ)
              <NumberInput
                type="number"
                value={config.trueSkillSpread}
                min={1}
                max={20}
                step={0.5}
                onChange={(e) =>
                  setConfig((c) => ({
                    ...c,
                    trueSkillSpread: Number(e.target.value),
                  }))
                }
              />
            </TunerLabel>
          </TunerGrid>
        </SimConfigCol>

        <SimConfigCol>
          <h3>Scoring Parameters</h3>
          <ScoringTuner
            config={config.scoring}
            onChange={(scoring) => setConfig((c) => ({ ...c, scoring }))}
          />
        </SimConfigCol>
      </SimConfigGrid>

      <FormRow style={{ marginTop: 16 }}>
        <Button onClick={run} disabled={running}>
          {running ? "⏳ Running..." : "▶️ Run Simulation"}
        </Button>
        {result && !running && (
          <span style={{ color: theme.colors.textDim, fontSize: "0.8rem" }}>
            {result.config.numPlayers} players × {result.config.numMatches} matches
          </span>
        )}
      </FormRow>

      {result && (
        <SimResults>
          <TabBar>
            <Tab active={activeTab === "results"} onClick={() => setActiveTab("results")}>
              📊 Results
            </Tab>
            <Tab active={activeTab === "advanced"} onClick={() => setActiveTab("advanced")}>
              🔍 Advanced
            </Tab>
            <Tab active={activeTab === "sweep"} onClick={() => setActiveTab("sweep")}>
              🔬 Sweep
            </Tab>
            <Tab active={activeTab === "ab"} onClick={() => setActiveTab("ab")}>
              ⚖️ A/B
            </Tab>
            <Tab active={activeTab === "montecarlo"} onClick={() => setActiveTab("montecarlo")}>
              🎲 Monte Carlo
            </Tab>
            <Tab active={activeTab === "ga"} onClick={() => setActiveTab("ga")}>
              🧬 GA
            </Tab>
          </TabBar>

          {activeTab === "results" && (
            <>
              <AnalyticsSummary result={result} />
              <Charts2Col>
                <TrueVsEstimatedChart result={result} />
                <TrueVsPointsChart result={result} />
              </Charts2Col>
              <Charts2Col>
                <ConvergenceChart result={result} />
                <SigmaChart result={result} />
              </Charts2Col>
              <Charts2Col>
                <PointDistributionChart result={result} />
                <MultiplierImpactChart result={result} />
              </Charts2Col>
              <SkillTracesChart result={result} />
            </>
          )}

          {activeTab === "advanced" && (
            <>
              <Charts2Col>
                <WinRateByTierChart result={result} />
                <RankingStabilityChart result={result} />
              </Charts2Col>
              <Charts2Col>
                <PerPlayerConvergenceChart result={result} />
                <ConfidenceIntervalsChart result={result} />
              </Charts2Col>
              <HeadToHeadTable result={result} />
            </>
          )}

          {activeTab === "sweep" && <SweepPanel baseConfig={config} />}

          {activeTab === "ab" && <ABPanel baseConfig={config} />}

          {activeTab === "montecarlo" && <MonteCarloPanel baseConfig={config} />}

          {activeTab === "ga" && <GAPanel baseConfig={config} />}
        </SimResults>
      )}
    </GlassCard>
  );
}
